/**
 * The four layouts behind one entry point, each returning the same shape.
 *
 * One function rather than four exports because the choice is a view setting: a caller switches
 * between them on the same graph, and everything downstream - packing, hit-testing, the renderer -
 * has to be indifferent to which one ran. The differences that remain are the two the shape carries
 * on purpose: whether routes are straight or curved, and whether there is any structure to draw
 * behind them.
 */

import {
  toBundlePath,
  toDegrees,
  toHierarchy,
  toLinks,
  type Hierarchy,
  type Link,
} from '../graph/topology';
import type { NetworkGraph, NetworkLayout, Vec3 } from '../types';
import { toBundledSpherePositions, toSphereCageGuides } from './bundledSphere';
import { toConeTreePositions, toHierarchyGuides } from './coneTree';
import { toGroupPacking } from './groupPacking';
import { toGroupVolumes } from './groupVolumes';
import { createForceSolve } from './forceLayout3d';
import { toLayerGuides } from './layered';
import { toTypeGroupedHierarchy } from './typeGroupedHierarchy';
import { toTypeLayering } from './typeLayers';
import { sampleBSpline, sampleQuadratic, straighten } from './spline';
import { toUnitCube } from './unitCube';

export type NetworkLayoutKind =
  | 'force'
  | 'layered'
  | 'cone-tree'
  | 'bundled-sphere'
  | 'grouped';

export type BuildNetworkLayoutArgs = {
  graph: NetworkGraph;
  kind: NetworkLayoutKind;
  seed: number;
  /** Force iterations. The two tree layouts are closed-form and ignore it. */
  iterations: number;
  /** Holten's beta, for the bundled layout only. */
  bundleStrength: number;
  /** Multiplier on z after the unit-cube fit. */
  zStretch: number;
  /**
   * Relative size per mark, from `toRelativeNodeSizes`, parallel to `graph.nodes`.
   *
   * Taken rather than derived, and the same array the renderer draws from. The grouped layout
   * reserves space from these, so the two agreeing is what makes its non-overlap guarantee a
   * statement about pixels rather than about the packing's own private units.
   */
  nodeSizes: readonly number[];
};

/** Samples per curved route. Enough that a bundle reads as a curve at a full-screen zoom. */
const CURVE_SAMPLES = 20;

const SPHERE_RADIUS = 1;

/**
 * Clearance between packed sibling groups, as a fraction of their combined radii.
 *
 * Enough that two groups read as two rather than as one fused shape, and no more: every unit of
 * clearance is volume taken from the groups themselves, and a grouping drawn at arm's length from
 * itself is a picture of the gaps.
 */
const GROUP_SEPARATION = 0.12;

/**
 * How much of its reserved sphere a mark actually fills, in the grouped layout.
 *
 * Not 1. At 1 a mark is exactly the sphere the packing set aside for it, which is correct and reads
 * as a solid raft: `GROUP_SEPARATION` leaves 12% between a pair, and 12% of a small mark is a gap
 * the eye does not resolve. Four fifths leaves each mark visibly its own thing while keeping the
 * group as dense as the packing made it.
 *
 * It is also the only slack the guarantee has. The packing proves nothing overlaps at 1, so any
 * value at or below it cannot overlap either, whatever the estate's size - which is the whole
 * reason the radius is derived here rather than from `nodeScale`.
 */
const GROUP_MARK_FILL = 0.8;

/**
 * Distance between layer planes, in layout units.
 *
 * Large relative to what a plane spans, because the cube fit is uniform: the in-plane spread of a
 * few hundred nodes runs to tens of units, and at a spacing of 6 the four planes came out as a
 * sliver in the middle of the cube - a stack whose layers could not be told apart, which is the
 * one thing this layout exists to show.
 */
const LAYER_SPACING = 13;

/**
 * Charge scaled by the square root of degree.
 *
 * Linear in degree lets one hub dominate the whole field - a node with two hundred edges pushes
 * everything to the boundary and the layout becomes a shell. The square root still gives a hub the
 * room its neighbors need without that.
 */
const toCharges = (degrees: readonly number[]): number[] =>
  degrees.map(degree => 1 + Math.sqrt(degree));

/**
 * Parent-child pairs, as springs for the force layouts only.
 *
 * Not drawn, and not part of `links`. The reference graph is disconnected - a capability domain, a
 * platform root and a team root carry no references at all - and a force layout has nothing to say
 * about where a component with no edges belongs, so those six nodes were left wherever the seed
 * put them and floated clear of the estate. Containment is real structure and pulling on it both
 * fixes that and sits a capability next to the applications that realise it.
 */
const toContainmentLinks = (hierarchy: Hierarchy): Link[] =>
  hierarchy.childIndices.flatMap((children, parent) =>
    children.map(child => ({ source: parent, target: child }))
  );

/**
 * A layout being arrived at, advanced in batches by whoever is drawing it.
 *
 * Three of the five arrangements are closed form and answer in milliseconds; the two built on the
 * force solve take seconds at a few hundred marks, and run to completion in one call they hold the
 * main thread for all of it. So a layout is something a caller *drives* rather than something it
 * asks for, and the two that cost nothing simply arrive settled.
 *
 * `toLayout` is callable at any point and always answers a complete layout - fitted to the unit
 * cube, with its routes and guides derived - because a half-settled arrangement is a picture worth
 * drawing. Watching a force layout relax is how the reader learns which clusters were already
 * together, which a freeze followed by an answer does not show.
 */
export type NetworkLayoutSolve = {
  /** Runs up to `steps` more iterations, stopping once settled. */
  advance: (steps: number) => void;
  /** Iterations still to run; 0 for a closed-form arrangement, and once a solve has settled. */
  readonly remaining: number;
  /** The arrangement as it stands, complete and drawable. */
  toLayout: () => NetworkLayout;
};

/** A closed-form arrangement, which is settled the moment it is computed. */
const settled = (layout: NetworkLayout): NetworkLayoutSolve => ({
  advance: () => {},
  remaining: 0,
  toLayout: () => layout,
});

export const createNetworkLayoutSolve = ({
  graph,
  kind,
  seed,
  iterations,
  bundleStrength,
  zStretch,
  nodeSizes,
}: BuildNetworkLayoutArgs): NetworkLayoutSolve => {
  const nodeCount = graph.nodes.length;
  const { links, edgeIndices } = toLinks(graph);
  const hierarchy = toHierarchy(graph.nodes);
  /* Springs, not routes: these pull on the solve and are never drawn. */
  const solvedLinks = [...links, ...toContainmentLinks(hierarchy)];
  const straightRoutes = (positions: readonly Vec3[]): Vec3[][] =>
    links.map(({ source, target }) => [positions[source], positions[target]]);

  if (kind === 'force') {
    const solve = createForceSolve({
      nodeCount,
      links: solvedLinks,
      charges: toCharges(toDegrees(nodeCount, links)),
      iterations,
      seed,
      repulsion: 30,
      linkDistance: 2.2,
      linkStrength: 0.7,
      gravity: 0.06,
      theta: 0.9,
      fixedZ: null,
    });

    return {
      advance: solve.advance,
      get remaining() {
        return solve.remaining;
      },
      toLayout: () => {
        const positions = solve.toPositions();
        return toUnitCube({
          layout: {
            positions,
            links,
            edgeIndices,
            routes: straightRoutes(positions),
            guides: [],
            volumes: [],
            guideLinks: null,
            nodeRadii: null,
          },
          padding: 0.08,
          zStretch,
        });
      },
    };
  }

  if (kind === 'layered') {
    const { layerIndices, layerCount } = toTypeLayering(graph.nodes, links);
    const solve = createForceSolve({
      nodeCount,
      links: solvedLinks,
      charges: toCharges(toDegrees(nodeCount, links)),
      iterations,
      seed,
      /* Stronger than the free layout's, and the springs weaker. A plane has two dimensions to
         spread a layer across rather than three, so the same charge leaves it congested - and the
         springs are pulling mostly across planes, where they cannot do anything except drag
         everything toward one vertical line. */
      repulsion: 55,
      linkDistance: 1.6,
      linkStrength: 0.35,
      gravity: 0.08,
      theta: 0.9,
      fixedZ: layerIndices.map(layer => layer * LAYER_SPACING),
    });

    return {
      advance: solve.advance,
      get remaining() {
        return solve.remaining;
      },
      toLayout: () => {
        const positions = solve.toPositions();
        return toUnitCube({
          layout: {
            positions,
            links,
            edgeIndices,
            routes: straightRoutes(positions),
            guides: toLayerGuides(
              positions,
              layerIndices,
              /* Grid divisions, scaled to the stack: a taller stack is seen from further away, so a
                 fixed division count reads as a finer mesh on a short stack than on a tall one. */
              layerCount + 2,
              LAYER_SPACING
            ),
            volumes: [],
            guideLinks: null,
            nodeRadii: null,
          },
          padding: 0.08,
          zStretch,
        });
      },
    };
  }

  if (kind === 'cone-tree') {
    const containmentLinks = toContainmentLinks(hierarchy);
    const positions = toConeTreePositions({
      hierarchy,
      nodeCount,
      radialStep: 2.6,
      depthStep: 5.5,
    });

    /* Bent toward the axis, by a fraction of how far out the midpoint already is. A fixed offset
       would bend a short edge near the center as hard as a long one at the rim, and the short one
       would loop. */
    const routes = links.map(({ source, target }) => {
      const from = positions[source];
      const to = positions[target];
      const midpoint: Vec3 = [
        (from[0] + to[0]) / 2,
        (from[1] + to[1]) / 2,
        (from[2] + to[2]) / 2,
      ];

      return sampleQuadratic(
        from,
        [midpoint[0] * 0.45, midpoint[1] * 0.45, midpoint[2]],
        to,
        CURVE_SAMPLES
      );
    });

    return settled(
      toUnitCube({
        layout: {
          positions,
          links,
          edgeIndices,
          routes,
          /* Derived from the pairs rather than beside them, so what is drawn now and what a
           movement re-derives cannot come from two different walks of the hierarchy. */
          guides: toHierarchyGuides(containmentLinks, positions),
          guideLinks: containmentLinks,
          volumes: [],
          nodeRadii: null,
        },
        padding: 0.08,
        zStretch,
      })
    );
  }

  if (kind === 'grouped') {
    /**
     * Positions come from the grouping, not from the edges, and that is the whole point of it.
     *
     * The other four arrange by structure the references imply. This one arranges by structure a
     * reader declared, and answers a different question: not "what clusters" but "does what I said
     * belongs together sit together". Which is why it is a layout rather than a modifier on the
     * other four - grouping a force solve means constraining the solve, and a constrained solve is
     * a different piece of work from this one.
     */
    const packing = toGroupPacking({
      hierarchy,
      nodeCount,
      /* The drawn sizes themselves. Scale-free: the packing's extent scales with these and
         `toUnitCube` divides it back out, so only the ratios matter here. */
      leafRadii: nodeSizes,
      separation: GROUP_SEPARATION,
    });

    /* Routed along the grouping, so references between two groups share the middle of their path
       and braid rather than crossing as a hairball - the mechanism the bundled sphere uses, over a
       tree a reader declared instead of one derived from the types. */
    const groupedRoutes = links.map(({ source, target }) =>
      sampleBSpline(
        straighten(
          toBundlePath(hierarchy, source, target).map(
            index => packing.positions[index]
          ),
          bundleStrength
        ),
        CURVE_SAMPLES
      )
    );

    return settled(
      toUnitCube({
        layout: {
          positions: packing.positions,
          links,
          edgeIndices,
          routes: groupedRoutes,
          /* No cage. A group is drawn as the space it occupies rather than as a wireframe around it -
           see `NetworkVolume` for why the outline was the wrong primitive for a nested region. */
          guides: [],
          guideLinks: null,
          volumes: toGroupVolumes({
            hierarchy,
            spheres: packing.spheres,
            nodeSizes,
          }),
          /* The sizes the packing reserved with, which for a leaf is exactly the sphere it set
           aside. Read off `nodeSizes` rather than off `packing.spheres`, because a sphere at an
           interior node is that group's whole *region* - taking radii from there drew every
           expanded group as one enormous mark at its own centre.

           An interior node gets the mark size it would have had as a leaf, which for the
           weightless group the app sends is nothing. A component that has children *and* a weight
           is drawn over them, as it was before this: the packing gave its centre to a region, so
           there is no reservation there to draw instead. */
          nodeRadii: nodeSizes.map(size => size * GROUP_MARK_FILL),
        },
        padding: 0.08,
        zStretch,
      })
    );
  }

  const {
    hierarchy: groupedHierarchy,
    indexCount,
    groupIndices,
  } = toTypeGroupedHierarchy(graph.nodes, hierarchy);

  const positions = toBundledSpherePositions({
    hierarchy: groupedHierarchy,
    nodeCount: indexCount,
    radius: SPHERE_RADIUS,
    innerRadiusFraction: 0.3,
    /* Sectors only when there are groups to size them by. Ungrouped, the roots are the components
       themselves, and a wedge each would be a wedge per component. */
    sectorRootIndices: groupIndices.length > 0 ? groupIndices : null,
  });

  const routes = links.map(({ source, target }) =>
    sampleBSpline(
      straighten(
        toBundlePath(groupedHierarchy, source, target).map(
          index => positions[index]
        ),
        bundleStrength
      ),
      CURVE_SAMPLES
    )
  );

  return settled(
    toUnitCube({
      layout: {
        /* The type groups routed the bundles and are not drawn: `positions` is parallel to
         `graph.nodes`, and a group has no component behind it to hover or hit-test. */
        positions: positions.slice(0, nodeCount),
        links,
        edgeIndices,
        routes,
        guides: toSphereCageGuides(SPHERE_RADIUS, 64),
        volumes: [],
        guideLinks: null,
        nodeRadii: null,
      },
      padding: 0.08,
      zStretch,
    })
  );
};

/**
 * The settled arrangement, in one call.
 *
 * What every caller that is not animating wants - a story, a test, an export - and what this was
 * before a layout became something to drive.
 */
export const buildNetworkLayout = (
  args: BuildNetworkLayoutArgs
): NetworkLayout => {
  const solve = createNetworkLayoutSolve(args);
  solve.advance(args.iterations);
  return solve.toLayout();
};
